<template>
  <Teleport to="body">
    <div
      v-if="attachment"
      class="modal-backdrop attachment-preview-backdrop"
      role="presentation"
      @click.self="emit('close')"
    >
      <section class="modal attachment-preview-modal" role="dialog" aria-modal="true" :aria-label="`预览 ${attachment.filename}`">
        <header>
          <div class="attachment-preview-heading">
            <span class="eyebrow">附件预览</span>
            <h3>{{ attachment.filename }}</h3>
            <span>{{ formatBytes(attachment.size_bytes) }}</span>
          </div>
          <button class="icon-button" title="关闭" aria-label="关闭附件预览" @click="emit('close')">
            <X :size="18" />
          </button>
        </header>

        <div class="attachment-preview-body">
          <div v-if="loading" class="attachment-preview-state" role="status">
            <LoaderCircle class="spinning" :size="28" />
            <span>正在加载附件…</span>
          </div>
          <div v-else-if="errorMessage" class="attachment-preview-state">
            <FileWarning :size="34" />
            <strong>附件加载失败</strong>
            <span>{{ errorMessage }}</span>
            <button type="button" @click="loadAttachment"><RotateCcw :size="16" />重试</button>
          </div>
          <img
            v-else-if="previewKind === 'image' && !renderFailed"
            class="attachment-preview-image"
            :src="displayImageUrl"
            :alt="attachment.filename"
            @error="onImageError"
          />
          <iframe
            v-else-if="previewKind === 'pdf'"
            class="attachment-preview-frame"
            :src="objectUrl"
            :title="attachment.filename"
          />
          <div v-else-if="previewKind === 'text'" class="attachment-preview-text-wrap">
            <pre class="attachment-preview-text">{{ textContent }}</pre>
            <p v-if="textTruncated" class="attachment-preview-note">文件较大，仅显示前 2 MiB；可下载后查看完整内容。</p>
          </div>
          <div v-else class="attachment-preview-state">
            <File :size="42" />
            <strong>{{ renderFailed ? "浏览器无法显示这张图片" : "此类型不支持站内预览" }}</strong>
            <span>可以在新标签页中打开，或下载后使用本地应用查看。</span>
          </div>
        </div>

        <footer class="modal-actions attachment-preview-actions">
          <button
            v-if="previewKind === 'image' && !renderFailed && !showingOriginal"
            type="button"
            @click="viewOriginal"
          >
            <LoaderCircle v-if="originalRequested && originalLoading" class="spinning" :size="17" />
            <RotateCcw v-else-if="originalError" :size="17" />
            {{ originalButtonLabel }}
          </button>
          <button
            v-if="previewKind === 'pdf' || previewKind === 'unsupported' || renderFailed"
            type="button"
            :disabled="!externalUrl"
            @click="openExternal"
          >
            <ExternalLink :size="17" />新标签页打开
          </button>
          <button type="button" :disabled="!downloadAvailable || downloading" @click="download">
            <LoaderCircle v-if="downloading" class="spinning" :size="17" />
            <Download v-else :size="17" />{{ downloading ? "正在准备…" : "下载" }}
          </button>
          <button type="button" class="primary" @click="emit('close')">关闭</button>
        </footer>
      </section>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from "vue";
import { Download, ExternalLink, File, FileWarning, LoaderCircle, RotateCcw, X } from "@lucide/vue";
import { api } from "../api";
import { attachmentThumbnailBlob } from "../attachmentImageCache";
import { formatBytes, readableError } from "../composables/format";
import type { Attachment } from "../types";

const TEXT_PREVIEW_LIMIT = 2 * 1024 * 1024;
const props = defineProps<{ attachment: Attachment | null }>();
const emit = defineEmits<{ close: [] }>();
const loading = ref(false);
const errorMessage = ref("");
const objectUrl = ref("");
const thumbnailUrl = ref("");
const originalUrl = ref("");
const textContent = ref("");
const textTruncated = ref(false);
const renderFailed = ref(false);
const originalLoading = ref(false);
const originalRequested = ref(false);
const originalError = ref("");
const showingOriginal = ref(false);
const downloading = ref(false);
let loadVersion = 0;
let originalController: AbortController | null = null;
let originalPromise: Promise<boolean> | null = null;

const previewKind = computed<"image" | "pdf" | "text" | "unsupported">(() => {
  const mime = props.attachment?.mime_type || "";
  if (mime.startsWith("image/")) return "image";
  if (mime === "application/pdf") return "pdf";
  if (mime.startsWith("text/")) return "text";
  return "unsupported";
});
const displayImageUrl = computed(() => showingOriginal.value ? originalUrl.value : thumbnailUrl.value);
const externalUrl = computed(() => previewKind.value === "image" ? (originalUrl.value || thumbnailUrl.value) : objectUrl.value);
const downloadAvailable = computed(() => previewKind.value === "image" ? Boolean(thumbnailUrl.value) : Boolean(objectUrl.value));
const originalButtonLabel = computed(() => {
  if (originalRequested.value && originalLoading.value) return "原图加载中…";
  if (originalError.value) return "重试原图";
  return "查看原图";
});

watch(() => props.attachment?.id, loadAttachment, { immediate: true });
onMounted(() => window.addEventListener("keydown", onKeydown));
onUnmounted(() => {
  window.removeEventListener("keydown", onKeydown);
  clearOwnedUrls();
});

async function loadAttachment() {
  const attachment = props.attachment;
  loadVersion += 1;
  const currentVersion = loadVersion;
  clearOwnedUrls();
  errorMessage.value = "";
  textContent.value = "";
  textTruncated.value = false;
  renderFailed.value = false;
  originalRequested.value = false;
  originalError.value = "";
  showingOriginal.value = false;
  downloading.value = false;
  if (!attachment) {
    loading.value = false;
    return;
  }

  loading.value = true;
  try {
    if (previewKind.value === "image") {
      const thumbnail = await attachmentThumbnailBlob(attachment.id);
      if (currentVersion !== loadVersion || props.attachment?.id !== attachment.id) return;
      thumbnailUrl.value = URL.createObjectURL(thumbnail);
      loading.value = false;
      void startOriginalLoad(attachment, currentVersion);
      return;
    }

    const blob = await api.attachmentBlob(attachment.id, "original");
    if (currentVersion !== loadVersion || props.attachment?.id !== attachment.id) return;
    objectUrl.value = URL.createObjectURL(blob);
    if (previewKind.value === "text") {
      textContent.value = await blob.slice(0, TEXT_PREVIEW_LIMIT).text();
      textTruncated.value = blob.size > TEXT_PREVIEW_LIMIT;
    }
  } catch (error) {
    if (currentVersion === loadVersion) errorMessage.value = readableError(error);
  } finally {
    if (currentVersion === loadVersion) loading.value = false;
  }
}

function clearOwnedUrls() {
  originalController?.abort();
  originalController = null;
  originalPromise = null;
  if (objectUrl.value) URL.revokeObjectURL(objectUrl.value);
  if (thumbnailUrl.value) URL.revokeObjectURL(thumbnailUrl.value);
  if (originalUrl.value) URL.revokeObjectURL(originalUrl.value);
  objectUrl.value = "";
  thumbnailUrl.value = "";
  originalUrl.value = "";
  originalLoading.value = false;
}

function openExternal() {
  if (!externalUrl.value) return;
  window.open(externalUrl.value, "_blank", "noopener,noreferrer");
}

async function download() {
  const attachment = props.attachment;
  if (!attachment || downloading.value) return;
  downloading.value = true;
  if (previewKind.value === "image" && !originalUrl.value) {
    if (!originalPromise) void startOriginalLoad(attachment, loadVersion);
    await originalPromise;
  }
  const url = previewKind.value === "image" ? originalUrl.value : objectUrl.value;
  downloading.value = false;
  if (!url) return;
  const link = document.createElement("a");
  link.href = url;
  link.download = attachment.filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
}

function viewOriginal() {
  const attachment = props.attachment;
  if (!attachment) return;
  originalRequested.value = true;
  if (originalUrl.value) {
    showingOriginal.value = true;
    return;
  }
  if (!originalPromise) void startOriginalLoad(attachment, loadVersion);
}

function startOriginalLoad(attachment: Attachment, currentVersion: number): Promise<boolean> {
  originalController?.abort();
  const controller = new AbortController();
  originalController = controller;
  originalLoading.value = true;
  originalError.value = "";
  const promise = api.attachmentBlob(attachment.id, "original", controller.signal)
    .then((blob) => {
      if (currentVersion !== loadVersion || props.attachment?.id !== attachment.id) return false;
      originalUrl.value = URL.createObjectURL(blob);
      if (originalRequested.value) showingOriginal.value = true;
      return true;
    })
    .catch((error) => {
      if (currentVersion === loadVersion && !isAbortError(error)) originalError.value = readableError(error);
      return false;
    })
    .finally(() => {
      if (currentVersion === loadVersion) originalLoading.value = false;
      if (originalPromise === promise) originalPromise = null;
    });
  originalPromise = promise;
  return promise;
}

function onImageError() {
  if (showingOriginal.value) {
    showingOriginal.value = false;
    originalError.value = "浏览器无法显示原图";
    return;
  }
  renderFailed.value = true;
}

function isAbortError(error: unknown) {
  return error instanceof DOMException && error.name === "AbortError";
}

function onKeydown(event: KeyboardEvent) {
  if (event.key === "Escape" && props.attachment) {
    event.stopImmediatePropagation();
    emit("close");
  }
}
</script>
